import '../../base/buttons/as-icon-button';
import { liveAppData } from '../../../store/store.ts';
import styleText from './as-session-set.css?raw';

export class TrSessionSet extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private _nameDisplay: HTMLElement | null = null;
  private _nameInput: HTMLInputElement | null = null;
  private _fileInput: HTMLInputElement | null = null;

  private _removeProjectEffect: (() => void) | null = null;
  private _removeUnsavedEffect: (() => void) | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      TrSessionSet.sheet
    ];

    this.shadowRoot!.innerHTML = /*html*/`
      <div id="header-session-set">
        <as-icon-button
          file-name="header/file-new.png" black
          tooltip-dict-key="tooltip.session.new"
          tooltip-default-text="New session"
          name="new"
        ></as-icon-button>
        <as-icon-button
          file-name="header/restore.png" black
          tooltip-dict-key="tooltip.session.restore"
          tooltip-default-text="Restore session"
          name="restore"
        ></as-icon-button>
        <as-icon-button
          file-name="header/save.png" black
          tooltip-dict-key="tooltip.session.save"
          tooltip-default-text="Save session"
          name="save"
        ></as-icon-button>
        <div class="project-name">
          <span class="name-display">Untitled</span>
          <input class="name-input" type="text" value="Untitled" hidden>
        </div>
      </div>
      <input type="file" accept=".asproject,.zip" id="backup-restore">
    `;

    this._nameDisplay = this.shadowRoot!.querySelector('.name-display');
    this._nameInput = this.shadowRoot!.querySelector('.name-input');
    this._fileInput = this.shadowRoot!.querySelector('#backup-restore');

    this._setupListeners();
  }

  get projectTitle(): string {
    return liveAppData.currentProject?.metadata.name ?? 'Untitled';
  }

  set projectTitle(value: string) {
    if (liveAppData.currentProject) {
      liveAppData.currentProject.metadata.name = value;
      // Nested mutation bypasses the proxy set trap; fan it out so subscribers
      // like <as-export-step> re-sync their default file name.
      liveAppData.notify('currentProject');
      this._refreshDisplay();
    }
  }

  private _setupListeners(): void {
    const sr = this.shadowRoot!;

    sr.addEventListener('as-button-click', (e: Event) => {
      if (document.querySelector('as-app')?.hasAttribute('disable-session-handlers')) return;
      switch ((e as CustomEvent).detail?.buttonName) {
        case 'new':
          this.dispatchEvent(new CustomEvent('new-session', { bubbles: true, composed: true }));
          break;
        case 'restore':
          this._fileInput?.click();
          break;
        case 'save':
          this.dispatchEvent(new CustomEvent('save-session', { bubbles: true, composed: true }));
          break;
      }
    });

    this._fileInput?.addEventListener('change', (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      (e.target as HTMLInputElement).value = '';
      if (!file) return;
      if (!document.querySelector('as-app')?.hasAttribute('disable-session-handlers')) {
        this.dispatchEvent(new CustomEvent('restore-session-file', {
          bubbles: true,
          composed: true,
          detail: { blob: file },
        }));
      }
    });

    this._nameDisplay?.addEventListener('click', () => this._startEdit());
    this._nameInput?.addEventListener('blur', () => this._commitEdit());
    this._nameInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this._commitEdit(); }
      if (e.key === 'Escape') this._cancelEdit();
    });
  }

  private _startEdit(): void {
    const current = liveAppData.currentProject?.metadata.name ?? 'Untitled';
    this._nameInput!.value = current;
    this._nameDisplay!.hidden = true;
    this._nameInput!.hidden = false;
    this._nameInput!.focus();
    this._nameInput!.select();
  }

  private _commitEdit(): void {
    const newName = this._nameInput!.value.trim() || 'Untitled';
    if (liveAppData.currentProject) {
      liveAppData.currentProject.metadata.name = newName;
      liveAppData.currentProject.metadata.updatedAt = new Date();
      // Nested mutation bypasses the proxy set trap; fan it out so subscribers
      // like <as-export-step> re-sync their default file name.
      liveAppData.notify('currentProject');
    }
    this._nameDisplay!.hidden = false;
    this._nameInput!.hidden = true;
    this._refreshDisplay();
  }

  private _cancelEdit(): void {
    this._nameDisplay!.hidden = false;
    this._nameInput!.hidden = true;
  }

  private _refreshDisplay(): void {
    const name = liveAppData.currentProject?.metadata.name || 'Untitled';
    const unsaved = liveAppData.unsavedChanges;
    if (this._nameDisplay) {
      this._nameDisplay.textContent = unsaved ? `${name} *` : name;
      this._nameDisplay.classList.toggle('unsaved', unsaved);
    }
  }

  connectedCallback(): void {
    this._removeProjectEffect = liveAppData.addSideEffect('currentProject', () => this._refreshDisplay());
    this._removeUnsavedEffect = liveAppData.addSideEffect('unsavedChanges', () => this._refreshDisplay());
    this._refreshDisplay();
  }

  disconnectedCallback(): void {
    this._removeProjectEffect?.();
    this._removeUnsavedEffect?.();
  }
}

customElements.define('as-session-set', TrSessionSet);
